<template>
  <div class="custom-kpi-config">
    <!-- KPI : 1 mesure + (toggle ventilation) + 0 ou 1 dim + total -->
    <template v-if="widgetType === 'kpi'">
      <div class="cfg-section">
        <label class="cfg-label">{{ t('customStats.widgetConfig.measure.singleLabel') }}</label>
        <Dropdown
          :model-value="selectedMeasure"
          :options="measuresOptions"
          option-label="name"
          option-value="key"
          class="w-full"
          @update:model-value="onSelectSingleMeasure"
        />
      </div>

      <div v-if="dimensionsOptions.length > 0" class="cfg-section">
        <div class="cfg-toggle-row">
          <label class="cfg-label-inline">
            {{ t('customStats.widgetConfig.kpi.enableBreakdown') }}
          </label>
          <InputSwitch
            :model-value="kpiBreakdownEnabled"
            @update:model-value="onToggleKpiBreakdown"
          />
        </div>
      </div>

      <template v-if="kpiBreakdownEnabled">
        <div class="cfg-section">
          <label class="cfg-label">{{ t('customStats.widgetConfig.kpi.ventilateBy') }}</label>
          <Dropdown
            :model-value="selectedDimension"
            :options="dimensionsOptions"
            option-label="name"
            option-value="key"
            class="w-full"
            @update:model-value="onSelectKpiDimension"
          />
        </div>

        <div class="cfg-section">
          <div class="cfg-toggle-row">
            <label class="cfg-label-inline">
              {{ t('customStats.widgetConfig.kpi.includeTotal') }}
            </label>
            <InputSwitch
              :model-value="includeTotal"
              @update:model-value="onToggleIncludeTotal"
            />
          </div>
          <div v-if="includeTotal" class="cfg-toggle-row">
            <label class="cfg-label-inline">
              {{ t('customStats.widgetConfig.kpi.totalOnly') }}
            </label>
            <InputSwitch
              :model-value="totalOnly"
              @update:model-value="onToggleTotalOnly"
            />
          </div>
        </div>
      </template>
    </template>

    <!-- TABLE : N mesures + N dims (≥1 dim obligatoire) -->
    <template v-else-if="widgetType === 'table'">
      <div class="cfg-section">
        <label class="cfg-label">{{ t('customStats.widgetConfig.measure.multiLabel') }}</label>
        <MultiSelect
          :model-value="selectedMeasures"
          :options="measuresOptions"
          option-label="name"
          option-value="key"
          :show-toggle-all="true"
          :max-selected-labels="3"
          :placeholder="t('customStats.widgetConfig.measure.multiPlaceholder')"
          class="w-full"
          @update:model-value="onUpdateMeasures"
        />
      </div>

      <div class="cfg-section">
        <label class="cfg-label">
          {{ t('customStats.widgetConfig.dimension.multiLabel') }}
          <span class="cfg-label-required">*</span>
        </label>
        <MultiSelect
          :model-value="selectedDimensions"
          :options="dimensionsOptions"
          option-label="name"
          option-value="key"
          :show-toggle-all="true"
          :max-selected-labels="3"
          :placeholder="t('customStats.widgetConfig.dimension.multiPlaceholder')"
          class="w-full"
          @update:model-value="onUpdateDimensions"
        />
        <div v-if="selectedDimensions.length === 0" class="cfg-warning">
          {{ t('customStats.widgetConfig.table.dimensionRequired') }}
        </div>
      </div>
    </template>

    <!-- CHART : mode + mesures + dims + sub-type -->
    <template v-else-if="widgetType === 'chart'">
      <div class="cfg-section">
        <label class="cfg-label">{{ t('customStats.widgetConfig.chart.modeLabel') }}</label>
        <div class="cfg-toggle-group">
          <button
            v-if="supportsTimeline"
            type="button"
            class="cfg-toggle-btn"
            :class="{ 'cfg-toggle-btn--active': chartMode === 'timeline' }"
            @click="setChartMode('timeline')"
          >
            <i class="far fa-chart-line mr-2" />
            {{ t('customStats.widgetConfig.chart.modeTimeline') }}
          </button>
          <button
            v-if="dimensionsOptions.length > 0"
            type="button"
            class="cfg-toggle-btn"
            :class="{ 'cfg-toggle-btn--active': chartMode === 'distribution' }"
            @click="setChartMode('distribution')"
          >
            <i class="far fa-chart-pie mr-2" />
            {{ t('customStats.widgetConfig.chart.modeDistribution') }}
          </button>
        </div>
      </div>

      <!-- Timeline : multi-mesures + 0/1 dim de ventilation -->
      <template v-if="chartMode === 'timeline'">
        <div class="cfg-section">
          <label class="cfg-label">{{ t('customStats.widgetConfig.measure.multiLabel') }}</label>
          <MultiSelect
            :model-value="selectedMeasures"
            :options="measuresOptions"
            option-label="name"
            option-value="key"
            :show-toggle-all="true"
            :max-selected-labels="3"
            :placeholder="t('customStats.widgetConfig.measure.multiPlaceholder')"
            class="w-full"
            @update:model-value="onUpdateMeasures"
          />
        </div>

        <div v-if="dimensionsOptions.length > 0" class="cfg-section">
          <label class="cfg-label">{{ t('customStats.widgetConfig.chart.ventilateBy') }}</label>
          <Dropdown
            :model-value="selectedDimension"
            :options="kpiDimensionOptions"
            option-label="name"
            option-value="key"
            class="w-full"
            @update:model-value="onSelectChartDimension"
          />
        </div>

        <div class="cfg-section">
          <label class="cfg-label">{{ t('customStats.widgetConfig.chart.subTypeLabel') }}</label>
          <div class="cfg-toggle-group">
            <button
              type="button"
              class="cfg-toggle-btn"
              :class="{ 'cfg-toggle-btn--active': chartSubType === 'bar' }"
              @click="setChartSubType('bar')"
            >
              <i class="far fa-chart-column mr-2" />
              {{ t('customStats.widgetConfig.chart.subTypeBar') }}
            </button>
            <button
              type="button"
              class="cfg-toggle-btn"
              :class="{ 'cfg-toggle-btn--active': chartSubType === 'line' }"
              @click="setChartSubType('line')"
            >
              <i class="far fa-chart-line mr-2" />
              {{ t('customStats.widgetConfig.chart.subTypeLine') }}
            </button>
          </div>
        </div>
      </template>

      <!-- Distribution : 1 mesure + 1 dim obligatoire -->
      <template v-else-if="chartMode === 'distribution'">
        <div class="cfg-section">
          <label class="cfg-label">{{ t('customStats.widgetConfig.measure.singleLabel') }}</label>
          <Dropdown
            :model-value="selectedMeasure"
            :options="measuresOptions"
            option-label="name"
            option-value="key"
            class="w-full"
            @update:model-value="onSelectSingleMeasure"
          />
        </div>

        <div class="cfg-section">
          <label class="cfg-label">
            {{ t('customStats.widgetConfig.chart.distributionDim') }}
            <span class="cfg-label-required">*</span>
          </label>
          <Dropdown
            :model-value="selectedDimension"
            :options="dimensionsOptions"
            option-label="name"
            option-value="key"
            class="w-full"
            @update:model-value="onSelectDistributionDimension"
          />
        </div>

        <div class="cfg-section">
          <label class="cfg-label">{{ t('customStats.widgetConfig.chart.subTypeLabel') }}</label>
          <div class="cfg-toggle-group cfg-toggle-group--wrap">
            <button
              v-for="t in distributionSubTypes"
              :key="t.value"
              type="button"
              class="cfg-toggle-btn"
              :class="{ 'cfg-toggle-btn--active': chartSubType === t.value }"
              @click="setChartSubType(t.value)"
            >
              <i :class="`${t.icon} mr-2`" />
              {{ t.label }}
            </button>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, computed, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import InputSwitch from 'primevue/inputswitch';

interface CustomKpiMeasureMeta {
  key: string;
  name: string;
  type: string;
}
interface CustomKpiDimensionMeta {
  key: string;
  name: string;
}
export interface CustomKpiSelection {
  measures: string[];
  dimensions: string[];
  chartMode?: 'timeline' | 'distribution';
  chartSubType?: 'bar' | 'line' | 'pie' | 'doughnut' | 'radar';
}

export default defineComponent({
  name: 'CustomKpiWidgetDataConfig',
  components: {
    Dropdown,
    MultiSelect,
    InputSwitch,
  },
  props: {
    widgetType: {
      type: String as PropType<'kpi' | 'table' | 'chart'>,
      required: true,
    },
    measuresOptions: {
      type: Array as PropType<CustomKpiMeasureMeta[]>,
      required: true,
    },
    dimensionsOptions: {
      type: Array as PropType<CustomKpiDimensionMeta[]>,
      required: true,
    },
    supportsTimeline: {
      type: Boolean,
      default: true,
    },
    /**
     * Sélection courante (mesures + dimensions + chart options).
     * Mise à jour via update:selection.
     */
    selection: {
      type: Object as PropType<CustomKpiSelection>,
      required: true,
    },
    includeTotal: {
      type: Boolean,
      default: false,
    },
    totalOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:selection', 'update:includeTotal', 'update:totalOnly'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const selectedMeasures = computed(() => props.selection.measures ?? []);
    const selectedDimensions = computed(() => props.selection.dimensions ?? []);
    const selectedMeasure = computed(() => selectedMeasures.value[0] ?? null);
    const selectedDimension = computed(() => selectedDimensions.value[0] ?? null);

    const chartMode = computed(() => props.selection.chartMode ?? 'timeline');
    const chartSubType = computed(() => props.selection.chartSubType ?? 'bar');

    const kpiDimensionOptions = computed(() => [
      { key: '', name: t('customStats.widgetConfig.kpi.ventilateNone') },
      ...props.dimensionsOptions,
    ]);

    const distributionSubTypes = computed(() => [
      { value: 'pie', label: t('customStats.widgetConfig.chart.subTypePie'), icon: 'far fa-chart-pie' },
      { value: 'doughnut', label: t('customStats.widgetConfig.chart.subTypeDoughnut'), icon: 'fas fa-circle-notch' },
      { value: 'radar', label: t('customStats.widgetConfig.chart.subTypeRadar'), icon: 'far fa-chart-radar' },
      { value: 'bar', label: t('customStats.widgetConfig.chart.subTypeBar'), icon: 'far fa-chart-column' },
    ]);

    const updateSelection = (patch: Partial<CustomKpiSelection>) => {
      emit('update:selection', { ...props.selection, ...patch });
    };

    function onSelectSingleMeasure(key: string) {
      updateSelection({ measures: key ? [key] : [] });
    }

    function onUpdateMeasures(values: string[] | null) {
      updateSelection({ measures: values ?? [] });
    }

    function onUpdateDimensions(values: string[] | null) {
      updateSelection({ dimensions: values ?? [] });
    }

    const kpiBreakdownEnabled = computed(() => selectedDimensions.value.length > 0);

    function onToggleKpiBreakdown(v: boolean) {
      if (v) {
        if (selectedDimensions.value.length === 0 && props.dimensionsOptions[0]) {
          updateSelection({ dimensions: [props.dimensionsOptions[0].key] });
        }
      } else {
        updateSelection({ dimensions: [] });
        if (props.includeTotal) emit('update:includeTotal', false);
        if (props.totalOnly) emit('update:totalOnly', false);
      }
    }

    function onSelectKpiDimension(key: string) {
      updateSelection({ dimensions: key ? [key] : [] });
    }

    function onSelectChartDimension(key: string) {
      updateSelection({ dimensions: key ? [key] : [] });
    }

    function onSelectDistributionDimension(key: string) {
      updateSelection({ dimensions: key ? [key] : [] });
    }

    function setChartMode(mode: 'timeline' | 'distribution') {
      const patch: Partial<CustomKpiSelection> = { chartMode: mode };
      // Reset cohérent : si on passe en distribution, 1 seule mesure & 1 dim ;
      // si timeline, on peut garder multi-mesures.
      if (mode === 'distribution') {
        if (selectedMeasures.value.length > 1) {
          patch.measures = selectedMeasures.value.slice(0, 1);
        }
        if (selectedDimensions.value.length === 0) {
          patch.dimensions = props.dimensionsOptions[0]
            ? [props.dimensionsOptions[0].key]
            : [];
        } else if (selectedDimensions.value.length > 1) {
          patch.dimensions = selectedDimensions.value.slice(0, 1);
        }
        // Sub-type par défaut pour distribution
        if (
          chartSubType.value !== 'pie' &&
          chartSubType.value !== 'doughnut' &&
          chartSubType.value !== 'radar' &&
          chartSubType.value !== 'bar'
        ) {
          patch.chartSubType = 'pie';
        }
      } else {
        // Timeline : sub-type bar par défaut
        if (chartSubType.value !== 'bar' && chartSubType.value !== 'line') {
          patch.chartSubType = 'bar';
        }
      }
      updateSelection(patch);
    }

    function setChartSubType(t: string) {
      updateSelection({ chartSubType: t as any });
    }

    function onToggleIncludeTotal(v: boolean) {
      emit('update:includeTotal', v);
      if (!v) emit('update:totalOnly', false);
    }

    function onToggleTotalOnly(v: boolean) {
      emit('update:totalOnly', v);
    }

    return {
      t,
      selectedMeasures,
      selectedDimensions,
      selectedMeasure,
      selectedDimension,
      chartMode,
      chartSubType,
      kpiDimensionOptions,
      distributionSubTypes,
      kpiBreakdownEnabled,
      onToggleKpiBreakdown,
      onSelectSingleMeasure,
      onUpdateMeasures,
      onUpdateDimensions,
      onSelectKpiDimension,
      onSelectChartDimension,
      onSelectDistributionDimension,
      setChartMode,
      setChartSubType,
      onToggleIncludeTotal,
      onToggleTotalOnly,
    };
  },
});
</script>

<style lang="scss" scoped>
.custom-kpi-config {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cfg-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.cfg-label {
  font-weight: 500;
  font-size: 0.9rem;
  color: #374151;
}
.cfg-label-required {
  color: #dc2626;
  margin-left: 0.25rem;
}
.cfg-label-inline {
  font-weight: 500;
  font-size: 0.9rem;
  color: #374151;
  flex: 1;
}
.cfg-toggle-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
}
.cfg-checkbox-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.cfg-checkbox-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cfg-checkbox-label {
  font-size: 0.9rem;
  cursor: pointer;
}
.cfg-warning {
  color: #b45309;
  background: #fef3c7;
  border-radius: 4px;
  padding: 0.4rem 0.6rem;
  font-size: 0.85rem;
}
.cfg-toggle-group {
  display: inline-flex;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 8px;
  gap: 4px;
  flex-wrap: nowrap;

  &--wrap {
    flex-wrap: wrap;
  }
}
.cfg-toggle-btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  color: #6b7280;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    color: #111827;
  }
  &--active {
    background: white;
    color: #111827;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
